import { useEffect, useMemo, useState } from 'react';
import { MapPin, RefreshCw, Search } from 'lucide-react';
import { distanceKm, formatDateTime } from '@nu/shared';
import { api } from '../../api/client.js';
import Modal from '../../components/Modal.jsx';
import { problem } from '../bookings/util.js';
import { clockAfter, hm, nowHm } from './nearby.js';
import PlanMap from './PlanMap.jsx';

// Office: who surveys the picked jobs. Everyone (or each team) nearest first,
// straight line from the middle of the jobs to where their van is now on
// Samsara (GET /api/surveys/nearby). Tick one person, several, or a team
// (ticks its members); everyone ticked gets the jobs in their app. With
// someone ticked: how long the jobs take from their van (10 minutes each
// plus the drive, routed) and when they'd finish setting off now. Nothing
// reaches their app until Assign.
const km = (n) => `${n < 10 ? n.toFixed(1) : Math.round(n)} km`;

export default function AssignSurveyors({ open, jobs, allJobs, onClose, onAssign }) {
  const [tab, setTab] = useState('people');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [chosen, setChosen] = useState([]);
  const [plan, setPlan] = useState(null); // { plan } | { error }

  const load = async () => {
    setLoading(true);
    setError(null);
    try { setData(await api('/surveys/nearby?fresh=1')); } catch (e) { setError(problem(e)); } finally { setLoading(false); }
  };
  useEffect(() => {
    if (!open) return;
    setQ('');
    setTab('people');
    // One job: start from who's on it now.
    setChosen(jobs.length === 1 ? [...(jobs[0].surveyorIds || [])] : []);
    load();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // The time it takes, for whoever is ticked (after a short pause).
  const jobKey = jobs.map((j) => j.id).join(',');
  const chosenKey = chosen.join(',');
  useEffect(() => {
    setPlan(null);
    if (!open || !chosen.length || !jobs.length || jobs.length > 60) return undefined;
    let live = true;
    const t = setTimeout(() => {
      api('/surveys/auto-plan', { method: 'POST', body: { personIds: chosen, jobIds: jobs.map((j) => j.id), exact: true } })
        .then((p) => { if (live) setPlan({ plan: p, start: nowHm() }); }, (e) => { if (live) setPlan({ error: problem(e) }); });
    }, 500);
    return () => { live = false; clearTimeout(t); };
  }, [open, jobKey, chosenKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // The middle of the picked jobs that have a position.
  const middle = useMemo(() => {
    const at = jobs.filter((j) => j.lat != null && j.lon != null);
    return at.length ? { lat: at.reduce((s, j) => s + j.lat, 0) / at.length, lon: at.reduce((s, j) => s + j.lon, 0) / at.length } : null;
  }, [jobs]);

  // Open surveys each person has already (still to survey).
  const openCount = useMemo(() => {
    const n = new Map();
    for (const j of allJobs) if (j.jobStatus === 'To Presite') for (const id of j.surveyorIds || []) n.set(id, (n.get(id) || 0) + 1);
    return n;
  }, [allJobs]);

  const people = useMemo(() => (data?.people || []).map((p) => ({
    ...p, km: middle && p.position ? distanceKm(middle, p.position) : null, open: openCount.get(p.id) || 0,
  })), [data, middle, openCount]);
  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const near = (a, b) => (a.km ?? 1e9) - (b.km ?? 1e9) || a.name.localeCompare(b.name, 'en-GB');
  const s = q.trim().toLowerCase();
  const shownPeople = people.filter((p) => !s || p.name.toLowerCase().includes(s)).sort(near);
  const teams = useMemo(() => (data?.teams || []).map((t) => {
    const members = t.memberIds.map((id) => byId.get(id)).filter(Boolean);
    const kms = members.map((m) => m.km).filter((x) => x != null);
    return { ...t, members, km: kms.length ? Math.min(...kms) : null };
  }).filter((t) => t.members.length), [data, byId]);
  const shownTeams = teams.filter((t) => !s || t.name.toLowerCase().includes(s) || t.members.some((m) => m.name.toLowerCase().includes(s))).sort(near);

  const toggle = (id) => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const teamOn = (t) => t.members.every((m) => chosen.includes(m.id));
  const toggleTeam = (t) => setChosen((c) => (teamOn(t) ? c.filter((id) => !t.memberIds.includes(id)) : [...c, ...t.memberIds.filter((id) => !c.includes(id))]));

  const where = (p) => {
    if (!p.position) return <span className="muted">No van location</span>;
    const old = p.position.at && Date.now() - new Date(p.position.at) > 6 * 3600_000;
    return (
      <span className={old ? 'warn-text' : 'muted'}>
        {p.km != null && <><MapPin size={13} aria-hidden="true" /> {km(p.km)} · </>}
        {p.position.van || 'Van'}{p.position.at ? ` · seen ${formatDateTime(p.position.at)}` : ''}
      </span>
    );
  };
  const names = chosen.map((id) => byId.get(id)?.name).filter(Boolean);

  return (
    <Modal
      open={open}
      wide
      title={`Assign ${jobs.length === 1 ? 'this survey' : `${jobs.length} surveys`}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          {jobs.some((j) => j.surveyorIds?.length) && (
            <button type="button" className="btn" onClick={() => onAssign([])}>Unassign</button>
          )}
          <button type="button" className="btn primary" disabled={!chosen.length} onClick={() => onAssign(chosen)}>
            {chosen.length ? `Assign to ${chosen.length === 1 ? names[0] || '1 person' : `${chosen.length} people`}` : 'Assign'}
          </button>
        </>
      )}
    >
      <div className="stack">
        <p className="small muted">
          Nearest first: straight line from {jobs.length === 1 ? 'the job' : 'the middle of the jobs'} to where each van is now (Samsara).
          {!middle && ' These jobs have no position, so the list is by name.'}
          {data && !data.located && ' Van locations aren’t set up on this server.'}
          {' '}Everyone ticked sees the jobs in their app.
        </p>
        <div className="assign-tools">
          <div className="view-toggle" role="tablist" aria-label="Assign to">
            <button type="button" role="tab" aria-selected={tab === 'people'} className={`btn${tab === 'people' ? ' active' : ''}`} onClick={() => setTab('people')}>People</button>
            <button type="button" role="tab" aria-selected={tab === 'teams'} className={`btn${tab === 'teams' ? ' active' : ''}`} onClick={() => setTab('teams')}>Teams</button>
          </div>
          <label className="search">
            <Search size={18} aria-hidden="true" />
            <input type="search" placeholder={tab === 'teams' ? 'Filter teams or names' : 'Filter by name'} value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <button type="button" className="btn with-icon" disabled={loading} onClick={load}>
            <RefreshCw size={16} aria-hidden="true" className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
        {names.length > 0 && <p className="small"><strong>Ticked:</strong> {names.join(', ')}</p>}
        {names.length > 0 && jobs.length <= 60 && (
          <div className="assign-estimate small">
            {!plan && <span className="muted">Working out the time…</span>}
            {plan?.error && <span className="muted">{plan.error}</span>}
            {plan?.plan && (
              <span>
                <strong>{plan.plan.count} survey{plan.plan.count === 1 ? '' : 's'}:</strong> {hm(plan.plan.surveySeconds)} on site + {hm(plan.plan.travelSeconds)} driving = <strong>{hm(plan.plan.totalSeconds)}</strong>.
                {' '}Finish about <strong>{clockAfter(plan.start, plan.plan.totalSeconds)}</strong> setting off now ({plan.start}) from {plan.plan.from.name}’s van.
                {plan.plan.count < jobs.length && ` ${jobs.length - plan.plan.count} not counted: their postcode couldn’t be found.`}
              </span>
            )}
            {plan?.plan && <PlanMap plan={plan.plan} start={plan.start} height={240} />}
          </div>
        )}
        {error && <p className="notice-line notice-bad">{error}</p>}
        {!data && !error && <p className="muted">Finding the vans…</p>}
        {data && tab === 'people' && (
          <ul className="pick-list assign-list">
            {shownPeople.map((p) => (
              <li key={p.id}>
                <label className={`pick assign-pick${chosen.includes(p.id) ? ' picked' : ''}`}>
                  <input type="checkbox" checked={chosen.includes(p.id)} onChange={() => toggle(p.id)} />
                  <span>
                    <strong>{p.name}</strong>
                    <span className="small"> {where(p)}</span>
                  </span>
                  <span className="small muted assign-open">{p.open ? `${p.open} to survey` : 'None yet'}</span>
                </label>
              </li>
            ))}
            {!shownPeople.length && <li className="muted small">Nobody matches.</li>}
          </ul>
        )}
        {data && tab === 'teams' && (
          <ul className="pick-list assign-list">
            {shownTeams.map((t) => (
              <li key={t.id}>
                <label className={`pick assign-pick${teamOn(t) ? ' picked' : ''}`}>
                  <input type="checkbox" checked={teamOn(t)} onChange={() => toggleTeam(t)} />
                  <span>
                    <strong>{t.name}</strong>{t.km != null && <span className="small muted"> · {km(t.km)}</span>}
                    {t.members.map((m) => <span key={m.id} className="small assign-member">{m.name} {where(m)}</span>)}
                  </span>
                  <span className="small muted assign-open">{t.members.reduce((n, m) => n + m.open, 0) || 'None'} to survey</span>
                </label>
              </li>
            ))}
            {!shownTeams.length && <li className="muted small">No teams match.</li>}
          </ul>
        )}
      </div>
    </Modal>
  );
}
